import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, CircularProgress, FormControl, styled, TextField } from '@mui/material';
import { useAppSelector } from 'setup/store';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import classes from './UserSelectionList.module.scss';
import { useUserActions } from 'actions/user-actions';
import { IUser } from 'common/interfaces/iuser';

type UserSelectionListProps = {
  onUserSelected: (user: IUser | undefined) => void;
  selectedUserId: number;
  label?: string;
  disabled?: boolean;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.primary.main, // Normal border color
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.dark, // On hover
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main, // On focus
      borderWidth: '2px',
    },
  },
}));
const UserSelectionList: FC<UserSelectionListProps> = ({ label, selectedUserId, disabled, onUserSelected }) => {
  const { t } = useTranslation();
  const appConfig = useAppSelector((state) => state.appReducer.appConfig);
  const loading = useAppSelector((store) => store.storeReducer.loading);
  const roleNameMap = useAppSelector((store) => store.userReducer.roleNameMap);
  const [_users, setUsers] = useState<readonly IUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser>({} as IUser);

  const [inputValue, setInputValue] = useState<string>('');

  const { searchUsers, loadAllRoles, loadUserDetails } = useUserActions();
  const refLastKey = useRef<string>('-1');

  useEffect(() => {
    if (!roleNameMap) {
      loadAllRoles();
    }
  }, [roleNameMap]);

  const _loadSelectedUser = async (userId: number) => {
    const data = await loadUserDetails(userId);
    if (data?.data?.id) {
      setSelectedUser(data.data);
    }
  };
  useEffect(() => {
    if (selectedUserId) {
      let suser = undefined;
      if (_users) {
        const usr = _users.find((user) => user.id === selectedUserId);
        if (usr) {
          suser = usr;
          setSelectedUser(usr);
        }
      }
      if (!suser) {
        _loadSelectedUser(selectedUserId);
      }
    }
  }, [selectedUserId]);

  const _searchUsers = async (inputValue: string) => {
    if (refLastKey.current !== inputValue) {
      const data = await searchUsers(1, 20, {
        term: inputValue,
        roleIds: roleNameMap ? [roleNameMap['franchisee_admin'].id] : [],
      });
      setUsers(data?.items || []);
      refLastKey.current = inputValue;
    }
  };

  useEffect(() => {
    if (inputValue) {
      _searchUsers(inputValue);
    }
  }, [inputValue]);

  const onSelectedUser = (user: IUser | undefined) => {
    setSelectedUser({} as IUser);
    onUserSelected && onUserSelected(user);
  };

  return (
    <Box>
      <FormControl style={{ width: '20%' }}>
        <Autocomplete
          id="AutoCompleteSelectUserSearch"
          options={_users}
          onOpen={() => _searchUsers('')}
          disabled={disabled}
          getOptionLabel={(option) => option.displayName || ''}
          onInputChange={(event, newInputValue, reason) => {
            console.log('onInputChange:', reason);
            if (reason !== 'reset') {
              setInputValue(newInputValue);
            }
          }}
          loading={loading}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={'usr:' + option.id} {...optionProps}>
                {/* {' #' + option.storeNumber + ' ' + option.storeName + ' zip: ' + option.zipcode} */}
                <span className={classes.storeOptionText}>{option.displayName + '::' + option.id}</span>
              </li>
            );
          }}
          value={selectedUser}
          // fullWidth
          // style={{ width: '100%' }}
          // style={{ border: '1px solid blueborder: 1px solid rgba(25, 118, 210, 0.5);', borderRadius: '4px' }}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={label || 'Users'}
              placeholder="Search User"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(event, newValue) => {
            onSelectedUser(newValue || undefined);
            // console.log('>>>>>>', newValue);
          }}
        />
      </FormControl>
    </Box>
  );
};

export default UserSelectionList;
